import type { MultibuySelectItem } from '@dcxwam/dcx-wam-ui-foundations'
import type {
  MultiBuyPriceEntry,
  ProductDataVariant
} from '../../../../services/product/product-data'
import type { MultibuyUomCategory } from '../../common/config'
import { formatPrice, FormatPriceOptions } from '../../../../utils/format-price'
import {
  getMultibuyUnitLabel,
  getMultibuyDefaultQty,
  getPricePerUnitLabel
} from '../../common/multibuy'

interface MultibuyConfig {
  multibuyEnabled?: boolean
  multibuyUomCategories?: MultibuyUomCategory[]
  multibuyBestValueLabel?: string
  multibuyBestValueToggle?: boolean
  multibuyGroupTitle?: string
}

interface BuildMultibuySelectParams {
  variant: ProductDataVariant
  shopConfig?: MultibuyConfig
  pageLocale: string
  stockLevel?: number
  formatPriceOptions?: FormatPriceOptions
}

export const buildMultibuySelectItems = ({
  variant,
  shopConfig,
  pageLocale,
  stockLevel,
  formatPriceOptions
}: BuildMultibuySelectParams): MultibuySelectItem[] | undefined => {
  const multiBuyPrice = variant?.price?.multiBuyPrice

  if (!shopConfig?.multibuyEnabled || !multiBuyPrice?.length) {
    return undefined
  }

  const currency = variant?.price?.priceCurrency

  const { singular, plural } = getMultibuyUnitLabel({
    categoryIdentifier: variant?.categoryIdentifier,
    unitOfMeasure: variant?.unitOfMeasure,
    multibuyUomCategories: shopConfig?.multibuyUomCategories
  })

  return multiBuyPrice.map((entry) => {
    const unitLabel = entry.qty === 1 ? singular : plural
    const attributeLabel = unitLabel
      ? `${entry.qty} ${unitLabel}`
      : `${entry.qty}`

    const formattedTotalPrice =
      formatPrice({
        value: entry.totalPrice,
        currency,
        locale: pageLocale,
        options: formatPriceOptions
      }) ?? ''

    const formattedUnitPrice =
      formatPrice({
        value: entry.unitPrice,
        currency,
        locale: pageLocale,
        options: formatPriceOptions
      }) ?? ''

    const pricePerUnitTemplate = getPricePerUnitLabel({
      categoryIdentifier: variant?.categoryIdentifier,
      unitOfMeasure: variant?.unitOfMeasure,
      multibuyUomCategories: shopConfig?.multibuyUomCategories
    })
    const unitPriceLabel =
      entry.qty > 1 && pricePerUnitTemplate
        ? pricePerUnitTemplate.replace('$unit_price$', formattedUnitPrice)
        : undefined

    const badge =
      entry.bestValue && shopConfig?.multibuyBestValueLabel
        ? shopConfig.multibuyBestValueLabel
        : undefined

    const disabled =
      stockLevel !== undefined && entry.qty > stockLevel ? true : undefined

    return {
      value: String(entry.qty),
      attributeLabel,
      price: formattedTotalPrice,
      unitPrice: unitPriceLabel,
      ...(badge ? { badge } : {}),
      ...(disabled ? { disabled } : {})
    }
  })
}

export const getMultibuyDefaultValue = (
  multiBuyPrice?: Array<{
    qty: number
    unitPrice: number
    totalPrice: number
    bestValue: boolean
  }>,
  bestValueToggle?: boolean,
  stockLevel?: number
): string | undefined => {
  if (!multiBuyPrice?.length) {
    return undefined
  }

  const qty = getMultibuyDefaultQty({
    multiBuyPrice,
    bestValueToggle,
    stockLevel
  })
  return String(qty)
}

interface MultibuyUnitPriceParams {
  hasMultibuy: boolean
  multiBuyPrice?: MultiBuyPriceEntry[]
  selectedQty?: number
  bestValueToggle?: boolean
  priceCurrency?: string
  pageLocale: string
  stockLevel?: number
  formatPriceOptions?: FormatPriceOptions
}

export const getMultibuyPriceForCard = ({
  hasMultibuy,
  multiBuyPrice,
  selectedQty,
  bestValueToggle,
  priceCurrency,
  pageLocale,
  stockLevel,
  formatPriceOptions
}: MultibuyUnitPriceParams): string | undefined => {
  if (!hasMultibuy || !multiBuyPrice?.length) {
    return undefined
  }

  const defaultQty = getMultibuyDefaultQty({
    multiBuyPrice,
    bestValueToggle,
    stockLevel
  })
  const resolvedQty = selectedQty ?? defaultQty

  const entry = resolvedQty
    ? multiBuyPrice.find((e) => e.qty === resolvedQty)
    : undefined

  if (!entry) {
    return undefined
  }

  return formatPrice({
    value: entry.totalPrice,
    currency: priceCurrency,
    locale: pageLocale,
    options: formatPriceOptions
  })
}
